/**
 * Часы задачи: два времени, с паузой и без.
 *
 * `elapsed` — то, что уйдёт в минуты дня: пока вкладка скрыта, счёт стоит.
 * `wall` — стенные часы от показа задачи до отправки, нужны для медиан по
 * шаблонам. Одно из другого не выводится, поэтому и считаются оба.
 *
 * Отдельным модулем, а не замыканием внутри экрана «Решение», по одной
 * причине: headless-браузер докладывает `visibilityState: "visible"` даже для
 * фоновой вкладки, то есть сквозная проба паузу не проверяет в принципе —
 * прогон 3 сентября 2026 дал `ms` и `msWall` одинаковыми и не сказал ничего.
 * Проверяется это `tests/clock.test.ts` на поддельном таймере.
 *
 * Счётчика на экране нет намеренно. Он появится, когда медиана по шаблону
 * будет посчитана на живых попытках, а не угадана; показать его сейчас значило
 * бы собрать времена, снятые ПОД счётчиком, — замер измерил бы сам себя, ровно
 * как контролёры, доли по которым ученику не показываются.
 */
export interface Clock {
  /** Вкладку скрыли: счёт зачтённого времени встал. */
  pause: () => void;
  /** Вернулись во вкладку. Повторный вызов ничего не ломает. */
  resume: () => void;
  /** Зачтённое время, мс: без промежутков, когда вкладка была скрыта. */
  elapsed: () => number;
  /** Стенные часы, мс: от создания часов до сейчас, пауз не знают. */
  wall: () => number;
}

export function newClock(now: () => number = Date.now): Clock {
  const startedAt = now();
  let counted = 0;
  let runningSince: number | null = startedAt;
  return {
    pause: () => {
      if (runningSince === null) return;
      counted += now() - runningSince;
      runningSince = null;
    },
    resume: () => {
      // Идемпотентно: `visibilitychange` приходит и на возврат фокуса без
      // ухода, и второй `resume` не должен сбрасывать точку отсчёта.
      if (runningSince === null) runningSince = now();
    },
    elapsed: () => counted + (runningSince === null ? 0 : now() - runningSince),
    wall: () => now() - startedAt,
  };
}
